/* eslint-disable react-hooks/exhaustive-deps */
import _ from 'lodash';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';

import FilterDrawer from '../ui/tables/FilterDrawer';
import useStorage from './useStorage';
import useFunctionKeys from './useFunctionKeys';

import postal from 'postal';

import { FilterStorageVersion, getInitialSearch } from '../filtering';

import ApplicationContext from '../ApplicationContext';
import useExternalScreenFilter from './useExternalScreenFilter';
import { TableColumn } from '../tableSettings';
import { XCellType } from '../framework.core/XExportColumn';
import { ListFilterType } from '../fms.core/constants';
import useDeepCompareEffect from './useDeepCompareEffect';
import fastStorage from '../fastStorage';
import { ListOrderBy } from '../components/common/AbstractListView';
import { StorageUpdateOptions } from './useFastStorage';

function FilterButtonProxyFactory({initialized, ...other}:any) {
  if (!initialized) {
    return null;
  }

  return <FilterButtonProxy {...other} />;
}

function FilterButtonProxy(props:any) {
  const filterProps = {
    ...props,
  }

  return (
    <FilterDrawer {...filterProps} />
  );
}

// function FilterRowProxyFactory({initialized, ...other}) {
//   if (!initialized) {
//     return null;
//   }
  
//   return <FilterRowProxy {...other} />
// }

// function FilterRowProxy(props) {
//   const { width, breakNo } = useIfmsColSizes();

//   const filterProps = {
//     width,
//     breakNo,
//     ...props,
//   }

//   return (
//     <FilterRow {...filterProps} />
//   );
// }


type PagingOptions = {
  pageSize?: number;
  pagePerBatch?: number;
};

/**
 * useFilterBar - helper hook for list screens to provide generic filtering feature
 *
 * @export
 * @param {*} tableSettings - list table settings from where to extract filtering configuration
 * @param {*} onClear - on filter clear callback
 * @param {*} customRenderers - optional custom renderers for filter. Used for overriding default rendering of filters
 * @param {*} initialOrderBy - initial order by column
 * @param {string} [pageSizePlaceholderId='ifms-list-page-size'] - placeholder in DOM where page size management control will be injected
 * @param size
 * @return {*}
 */
export default function useFilterBar(
  tableSettings: any,
  customRenderers: any,
  initialOrderBy: ListOrderBy,
  customStorageName = '',
  hidden = false,
  setListViewPageNum = (n: number) => { },
  pagingOptions?: PagingOptions,
  setListOrderBy?: (x: Partial<ListOrderBy>, options?: StorageUpdateOptions) => void
) {
  const appContext = useContext(ApplicationContext);
  useFunctionKeys({onEsc});

  const {setFilterForScreenExternally, setFilterForSearchModel} = useExternalScreenFilter();

  const channel = postal.channel('ifms-filter');
  const storageName = `${FilterStorageVersion}-${window.location.pathname}${window.location.search}${customStorageName !== '' ? `-${customStorageName}` : ''}`;

  const storageKey = `filter-${storageName}`;
  const storageExternalSetKey = `filter-external-set-${storageName}`;
  const storageExternalSetSourceKey = `filter-external-set-source-${storageName}`;
  const filterOrdersStorageKey = `filter-order-${storageName}`;

  const storageInitialize = !!initialOrderBy ? {...getInitialSearchWithPageSizeFromSettings(), ...initialOrderBy} : getInitialSearchWithPageSizeFromSettings();

  // In case search has been set externally by different screen, this is the default
  const [externalSetSearchInStorage, setExternalSetSearchInStorage, removeExternalSetSearchFromStorage] = useStorage(storageExternalSetKey, null);
  const [externalSetSearchSourceInStorage, setExternalSetSearchSourceInStorage, removeExternalSetSearchSourceFromStorage] = useStorage(storageExternalSetSourceKey, null);

  const [searchInStorage, _setSearchInStorage, removeSearchFromStorage] = useStorage(storageKey, storageInitialize);
  const [filterOrderInStorage, setFilterOrderInStorage, removeFilterOrderFromStorage] = useStorage(filterOrdersStorageKey, []);
  const storageRef = useRef(storageInitialize);

  const [search, _setSearch] = useState<any>(searchInStorage);
  const searchRef = useRef(null);
  const searchCountRef = useRef(0);

  const generalFilters = tableSettings?.columns.filter((x:TableColumn) => x.filterable && (x.filterTypeV2 === undefined || x.filterTypeV2 === XCellType.String || x.filterTypeV2 === XCellType.StrBool ));
  const dateFilters = tableSettings?.columns.filter((x:TableColumn) => x.filterable && (x.filterTypeV2 === XCellType.Date || x.filterTypeV2 === XCellType.DateTime || x.filterType === "date"));
  const numberFilters = tableSettings?.columns.filter((x:TableColumn) => x.filterable && (x.filterTypeV2 === XCellType.Double || x.filterType === "number"));
  const boolFilters = tableSettings?.columns.filter((x:TableColumn) => x.filterable && (x.filterTypeV2 === XCellType.Bool || x.filterType === "bool"));
  const noNullOrEmptySearches = tableSettings?.columns.filter((x:TableColumn) => !!x.noNullOrEmptySearch);
  
  const initialized = !!search;
  let filterSetterSubscription = useRef<ISubscriptionDefinition<any>|null>(null);
  let forceClearSubscription = useRef<ISubscriptionDefinition<any>|null>(null);

  useEffect(() => {
    subscribeChannels();    

    const initializedKey = `${storageKey}-order-initialized`; 
    if (!fastStorage[initializedKey]) {
    if (setListOrderBy) {
      if (externalSetSearchInStorage && externalSetSearchInStorage.orderBy) {
        setListOrderBy({
          orderBy: externalSetSearchInStorage.orderBy,
          orderByAscending: externalSetSearchInStorage.orderByAscending
        });
      } else if (searchInStorage && searchInStorage.orderBy) {
        setListOrderBy({
          orderBy: searchInStorage.orderBy,
          orderByAscending: searchInStorage.orderByAscending
        });
      }
    }
    fastStorage[initializedKey] = true;
    }

    return () => {
      unsubscribeChannels();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useDeepCompareEffect(() => {
    const isEqual = _.isEqual(search, searchInStorage);
    if (!isEqual) {
      const s = {
        ...searchInStorage,
      };
      searchRef.current = s;
      _setSearch(s);
      searchCountRef.current = searchCountRef.current + 1;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInStorage]);

  function subscribeChannels() {
    filterSetterSubscription.current = postal.subscribe({
      channel: 'ifms-filter',
      topic: 'ifms-filter.countsetter',
      callback: applyCount,
    });
    forceClearSubscription.current = postal.subscribe({
      channel: 'ifms-filter',
      topic: 'ifms-filter.forceclear',
      callback: () => forceClearRef.current(),    // 호출 시점에 최신 함수를 실행
    });
  }

  function unsubscribeChannels() {
    if (filterSetterSubscription.current) {
      postal.unsubscribe(filterSetterSubscription.current);
    }
    if (forceClearSubscription.current) {
      postal.unsubscribe(forceClearSubscription.current);
    }
  }

  function onEsc() {
    // Detect if the filter is opened
    const filterNode = document.getElementById('ifms-list-screen-filter');

    if (!filterNode) {
      return false;
    } else {
      channel.publish('ifms-filter.dismiss');
      return true;
    }
  }

  function setSearchInStorage(s:any, filterOrder:any|undefined = undefined) {
    if (filterOrder !== null && filterOrder !== undefined) {
      setFilterOrderInStorage(filterOrder);
    }
    storageRef.current = s;
    _setSearchInStorage(s);
  }

  function setOrderBy(orderBy:any) {
    setSearchInStorage({...storageRef.current, ...orderBy});
  }

  function setListFilterType(listType:ListFilterType){
    //setSearchInStorage({...storageRef.current, listType});
    setSearchInStorage({...searchInStorage, listType});
  }

  
  function setListFilterType2(listType2: number){
    setSearchInStorage({...searchInStorage, listType2});
  }

  function setListFilterType3(listType3: number){
    setSearchInStorage({...searchInStorage, listType3});
  }

    function setListFilterType4(listType4: number){
    setSearchInStorage({...searchInStorage, listType4});
  }

  function getInitialSearchWithPageSizeFromSettings() {
    const opts = pagingOptions ?? {};

    return {
      ...getInitialSearch(),
      pageSize: opts.pageSize ?? appContext.defaultPageSize,
      pagePerBatch: opts.pagePerBatch ?? 10,
    }
  }

  function runSearchValueExtractors(s:any) {
    let generalText1 = s.generalText1;
    let generalText2 = s.generalText2;
    let generalText3 = s.generalText3;

    let numberFrom = s.numberFrom;
    let numberTo = s.numberTo;
    let numberFrom1 = s.numberFrom1;
    let numberTo1 = s.numberTo1;
    let numberFrom2 = s.numberFrom2;
    let numberTo2 = s.numberTo2;

    let extras = s.extras;

    if (s.general1 && customRenderers) {
      const customRenderer = customRenderers.find((x:any) => x.accessor === s.general1);
      if (customRenderer && customRenderer.searchValueExtractor) {
        generalText1 = customRenderer.searchValueExtractor(s.generalText1);
      }
    }

    if (s.general2 && customRenderers) {
      const customRenderer = customRenderers.find((x:any) => x.accessor === s.general2);
      if (customRenderer && customRenderer.searchValueExtractor) {
        generalText2 = customRenderer.searchValueExtractor(s.generalText2);
      }
    }

    if (s.general3 && customRenderers) {
      const customRenderer = customRenderers.find((x:any) => x.accessor === s.general3);
      if (customRenderer && customRenderer.searchValueExtractor) {
        generalText3 = customRenderer.searchValueExtractor(s.generalText3);
      }
    }

    if (s.number && customRenderers) {
      const customRenderer = customRenderers.find((x:any) => x.accessor === s.number);
      if (customRenderer && customRenderer.searchValueExtractor) {
        const {from, to} = customRenderer.searchValueExtractor({from: s.numberFrom, to: s.numberTo});
        numberFrom = from;
        numberTo = to;
      }
    }
    if (s.number1 && customRenderers) {
      const customRenderer = customRenderers.find((x:any) => x.accessor === s.number1);
      if (customRenderer && customRenderer.searchValueExtractor) {
        const {from, to} = customRenderer.searchValueExtractor({from: s.numberFrom1, to: s.numberTo1});
        numberFrom1 = from;
        numberTo1 = to;
      }
    }
    if (s.number2 && customRenderers) {
      const customRenderer = customRenderers.find((x:any) => x.accessor === s.number2);
      if (customRenderer && customRenderer.searchValueExtractor) {
        const {from, to} = customRenderer.searchValueExtractor({from: s.numberFrom2, to: s.numberTo2});
        numberFrom2 = from;
        numberTo2 = to;
      }
    }

    if (extras && extras.length > 0 && customRenderers) {
      extras = extras.map((extra: any, i: number) => {
        const customRenderer = customRenderers.find((x: any) => x.accessor === extra.extraSlot);
        if (customRenderer && customRenderer.searchValueExtractor) {
          if (s.extras[i].generalText !== undefined) {
            return {
              ...extra,
              generalText: customRenderer.searchValueExtractor(extra.generalText)
            };
          } else if (s.extras[i].numberFrom || s.extras[i].numberTo) {
            return {
              ...extra,
              numberFrom: customRenderer.searchValueExtractor(extra.numberFrom),
              numberTo: customRenderer.searchValueExtractor(extra.numberTo)
            };
          }
        }
        return extra;
      });
    }

    return {
      ...s, generalText1, generalText2, generalText3,
      numberFrom, numberTo, numberFrom1, numberTo1, numberFrom2, numberTo2,extras};
  }

  const forceClearRef = useRef<() => void>(() => {});
  forceClearRef.current = forceClear;   // 매 렌더마다 최신 함수로 교체

  function forceClear() {
    if (setListOrderBy && !!initialOrderBy) setListOrderBy({...initialOrderBy});

    const keepExternal = !!externalSetSearchInStorage
      && window.location.pathname.includes('/acJournal/billings');

    const s = {
      ...getInitialSearchWithPageSizeFromSettings(),
      ...(keepExternal ? _.omit(externalSetSearchInStorage, ['orderBy', 'orderByAscending']) : {}),
      version: storageRef.current.version + 1,
    };

    setSearchInStorage(s, []);
    if (!keepExternal) {
      setExternalSetSearchInStorage(null);
      setExternalSetSearchSourceInStorage(null);
    }
    setListViewPageNum(1);
  }

  function clearFilters() {

    const s = !externalSetSearchInStorage
      ? { ...getInitialSearchWithPageSizeFromSettings(), version: storageRef.current.version + 1 }
      : { ...getInitialSearchWithPageSizeFromSettings(), version: storageRef.current.version + 1, ...externalSetSearchInStorage };
    setSearchInStorage(s, []);


    // Filter ComboBox clear
    const url = `${window.location.pathname}${window.location.search}`;

    if (url.includes('/common/joblistview/RPCSP/shipmentStatus')) {
      const keyMatch = _.keys(fastStorage).find(x => x.startsWith('shipStatus-cust-ListFilter'));
      if (keyMatch) {
        delete fastStorage[keyMatch];
      }

      const keyMatch2 = _.keys(fastStorage).find(x => x.startsWith('shipStatus-status-ListFilter'));
      if (keyMatch2) {
        delete fastStorage[keyMatch2];
      }

      const keyMatch3 = _.keys(fastStorage).find(x => x.startsWith('shipStatus-tracking-ListFilter'));
      if (keyMatch3) {
        delete fastStorage[keyMatch3];
      }

      const keyMatch4 = _.keys(fastStorage).find(x => x.startsWith('shipStatus-blType-ListFilter'));
      if (keyMatch4) {
        delete fastStorage[keyMatch4];
      }
    }

    if(url.includes('/common/joblistview/AICTR/aiCenter')){
      const keyMatch = _.keys(fastStorage).find(x => x.startsWith('aiCenter-result-ListFilter'));
      if (keyMatch) {
        delete fastStorage[keyMatch];
      }
    }

  }

  function clearFiltersFromStorage() {
    removeSearchFromStorage();
    removeFilterOrderFromStorage();
    removeExternalSetSearchFromStorage();
    removeExternalSetSearchSourceFromStorage();
  }

  function applyCount(count:any) {
    channel.publish('ifms-filter.count', { search: searchRef.current, count });
  }

  const filterProps = {
    initialized,
    customRenderers,
    generalFilters,
    dateFilters,
    numberFilters,
    boolFilters,
    noNullOrEmptySearches,
    onSearch: (s: any, filterOrder: any) => {
      if (setListOrderBy) setListOrderBy({ orderBy: s.orderBy, orderByAscending: s.orderByAscending });
      setSearchInStorage({ ...runSearchValueExtractors(s), version: storageRef.current.version + 1 }, filterOrder);
    },
    onClick: () => channel.publish('ifms-filter.filterPreset'),
    filterOrder: filterOrderInStorage,
    externalSetSearchInStorage,
    searchStorage: { ...searchInStorage },
    setFilterOrderInStorage,
    hidden,
  };

  return {
    // FilterRow: FilterRowProxyFactory.bind(this, filterProps),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // FilterRow: useMemo(() => FilterRowProxyFactory.bind(this, filterProps), [tableSettings, searchInStorage.version, initialized]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // @ts-ignore:next-line
    FilterButton: useMemo(() => FilterButtonProxyFactory.bind(this, filterProps), [tableSettings, initialized, searchCountRef.current]),
    // search: prepareSearchForApi(search),
    search,
    clearFilters,
    clearFiltersFromStorage,
    setOrderBy,
    setFilterForScreenExternally,
    setFilterForSearchModel,
    openedByF6: !!externalSetSearchInStorage,
    f6Origin: !!externalSetSearchInStorage ? externalSetSearchSourceInStorage : null,
    f6Query: externalSetSearchInStorage?.generalText1 ?? null,
    setListFilterType,
    setListFilterType2,
    setListFilterType3,
    setListFilterType4,
  };
}